import usePrevious from 'hooks/usePrevious'
import { startTransition, useCallback, useEffect, useState } from 'react'

export const METASEARCH_LOAD_MORE_BATCH_SIZE = 3

// Render secondary YMAL tiles progressively so SSR and initial hydration can
// prioritise the primary offer and immediately useful secondary results.
function useProgressiveYmalTileCount(
  listKey: string | undefined,
  sortMode: string,
  priorityTileCount: number,
  availableTileCount: number,
): [number, () => void] {
  const displayListKey = `${listKey ?? 'disabled'}:${sortMode}:${priorityTileCount}`
  const previousDisplayListKey = usePrevious(displayListKey, displayListKey)
  const hasDisplayListChanged = previousDisplayListKey !== displayListKey
  // May run one batch ahead of the available tiles: Load more clicked once every loaded
  // offer is shown fetches more, and those offers should appear as soon as they arrive.
  const [requestedTileCount, setRequestedTileCount] = useState(priorityTileCount)
  const currentRequestedTileCount = hasDisplayListChanged ? priorityTileCount : requestedTileCount
  const displayedTileCount = Math.max(priorityTileCount, Math.min(currentRequestedTileCount, availableTileCount))

  useEffect(() => {
    setRequestedTileCount(priorityTileCount)
  }, [displayListKey, priorityTileCount])

  const loadMore = useCallback(() => {
    startTransition(() => {
      setRequestedTileCount(currentTileCount => Math.max(
        Math.min(hasDisplayListChanged ? priorityTileCount : currentTileCount, availableTileCount),
        priorityTileCount,
      ) + METASEARCH_LOAD_MORE_BATCH_SIZE)
    })
  }, [availableTileCount, hasDisplayListChanged, priorityTileCount])

  return [displayedTileCount, loadMore]
}

export default useProgressiveYmalTileCount
